import {useRef, useState, type ReactNode} from 'react';
import {tr} from '../i18n/idioma';
import {aceptaArchivo, archivosSoltados, traeArchivos} from '../utilidades/archivos-soltados';

// Recuerda junto al boton que tambien se puede arrastrar.
export function PistaDeSoltar({multiple = false, carpeta = false}: {multiple?: boolean; carpeta?: boolean}) {
  return (
    <span className="text-xs text-apagado">
      {carpeta
        ? tr('o arrastra la carpeta aquí', 'or drag the folder here')
        : multiple
          ? tr('o arrástralos aquí', 'or drag them here')
          : tr('o arrástralo aquí', 'or drag it here')}
    </span>
  );
}

// Envuelve un campo de carga para que tambien reciba archivos arrastrados
// desde el computador, y se resalta mientras se arrastra encima. Sin onSoltar,
// lo soltado entra al campo de archivo de adentro como si se hubiera elegido
// con el boton (mismo accept, multiple y onChange). Las carpetas soltadas
// entran completas, con su ruta, y van siempre por onSoltar.
export function ZonaParaSoltar({
  onSoltar,
  accept,
  multiple,
  carpeta = false,
  deshabilitado = false,
  className = '',
  children,
}: {
  onSoltar?: (archivos: File[]) => void;
  accept?: string;
  multiple?: boolean;
  carpeta?: boolean;
  deshabilitado?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [encima, setEncima] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // dragenter y dragleave tambien llegan al pasar por los hijos: se cuentan.
  const capas = useRef(0);

  const salir = () => {
    capas.current = 0;
    setEncima(false);
  };

  return (
    <div
      className={`${className} rounded-md transition-colors ${encima ? 'bg-verde-claro outline-2 outline-offset-2 outline-verde outline-dashed' : ''}`}
      data-prueba="zona-para-soltar"
      onDragEnter={e => {
        if (deshabilitado || !traeArchivos(e.dataTransfer)) return;
        capas.current += 1;
        setEncima(true);
      }}
      onDragLeave={() => {
        capas.current = Math.max(0, capas.current - 1);
        if (!capas.current) setEncima(false);
      }}
      onDragOver={e => {
        if (deshabilitado || !traeArchivos(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDrop={e => {
        if (!traeArchivos(e.dataTransfer)) return;
        e.preventDefault();
        salir();
        const campo = e.currentTarget.querySelector<HTMLInputElement>('input[type="file"]');
        if (deshabilitado || campo?.disabled) return;
        const acepta = accept ?? campo?.accept;
        const varios = multiple ?? campo?.multiple ?? false;
        void archivosSoltados(e.dataTransfer, carpeta).then(todos => {
          // En una carpeta, cada carga decide que archivos le sirven.
          const sirven = carpeta ? todos : todos.filter(a => aceptaArchivo(a, acepta));
          if (!sirven.length) {
            setAviso(
              todos.length
                ? tr('Ese tipo de archivo no sirve aquí.', 'That kind of file does not work here.')
                : tr('No llegó ningún archivo.', 'No file arrived.'),
            );
            return;
          }
          setAviso(
            !varios && sirven.length > 1
              ? tr('Aquí va un solo archivo: se tomó el primero.', 'Only one file goes here: the first one was taken.')
              : null,
          );
          const elegidos = varios ? sirven : sirven.slice(0, 1);
          if (onSoltar) onSoltar(elegidos);
          else if (campo) ponerEnElCampo(campo, elegidos);
        });
      }}
    >
      {children}
      {aviso && <span className="ml-1 text-xs text-alerta">{aviso}</span>}
    </div>
  );
}

function ponerEnElCampo(campo: HTMLInputElement, archivos: File[]) {
  const datos = new DataTransfer();
  for (const a of archivos) datos.items.add(a);
  campo.files = datos.files;
  campo.dispatchEvent(new Event('change', {bubbles: true}));
}
